import { useState, useMemo } from 'react'
import { Input } from '~/shadcn/ui/input'
import { InputGroupText } from '~/shadcn/ui/input-group'
import { SliderStyled } from '~/@mid_ui/slide-styled/SliderStyled'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import { DEFAULT_MIN_ORDER_AMOUNT } from '~/@pages_investing/order/order-form/useOrderOfferDataQuery'
import { useProjectInvestmentProjection } from './useProjectInvestmentProjection'
import { ProjectCompoundedRevenueChart } from '../project-compounded-revenue-chart/ProjectCompoundedRevenueChart'

type Props = { project: ProjectSchema }

export function ProjectDetailChart(props: Props) {
  const { getProjectInvestmentChartData } = useProjectInvestmentProjection(
    props.project
  )
  const amountMin = Math.max(
    DEFAULT_MIN_ORDER_AMOUNT,
    props.project.amountMin || 0
  )
  const amountMax = Math.max(
    props.project.investmentPrincipalMax || 0,
    props.project.amountMax || 0
  )

  const [sliderValue, setSliderValue] = useState(amountMin)
  const [investmentAmount, setInvestmentAmount] = useState(amountMin)
  const [inputValue, setInputValue] = useState(
    amountMin.toLocaleString('sk-SK', { useGrouping: true })
  )

  const chartData = useMemo(
    () => getProjectInvestmentChartData(investmentAmount),
    [getProjectInvestmentChartData, investmentAmount]
  )

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const rawValue = e.target.value
    const cleanValue = rawValue
      .replace(/\s/g, '')
      .replace(/[^0-9]/g, '')
      .trim()

    if (cleanValue === '') {
      setInputValue('')
      setSliderValue(0)
      setInvestmentAmount(0)
      return
    }

    const numValue = parseInt(cleanValue) || amountMin
    const minMaxNumValue = Math.max(0, Math.min(amountMax, numValue))

    setSliderValue(minMaxNumValue)
    setInvestmentAmount(minMaxNumValue)

    if (numValue) {
      setInputValue(
        minMaxNumValue.toLocaleString('sk-SK', { useGrouping: true })
      )
    } else {
      setInputValue('')
    }
  }

  function handleInputBlur() {
    const cleanValue = inputValue.replace(/\s/g, '').trim()

    if (cleanValue === '') {
      setInputValue('')
      setSliderValue(0)
      setInvestmentAmount(0)
      return
    }

    let numValue = parseInt(cleanValue) || amountMin
    numValue = Math.max(amountMin, Math.min(amountMax, numValue))
    setInputValue(
      numValue.toLocaleString('sk-SK', {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    )
  }

  function handleSliderChange(_: any, newValue: number | number[]) {
    const value = newValue as number
    setSliderValue(value)
    setInputValue(
      value.toLocaleString('sk-SK', {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    )
  }

  function handleSliderChangeCommitted(_: any, newValue: number | number[]) {
    setInvestmentAmount(newValue as number)
  }

  return (
    <>
      <div className="md:px-6 md:pt-6 md:pb-4">
        <div className="flex flex-row justify-between items-center mb-3">
          <h6 className="font-medium text-border-strong">Investícia</h6>
          <h4 className="font-bold text-gray-900">
            <Input
              value={inputValue}
              max={amountMax}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInputBlur()
                }
              }}
              rightElement={
                <InputGroupText className="text-xl font-semibold text-primary">
                  €
                </InputGroupText>
              }
              className="text-xl md:text-xl bg-transparent text-right rounded max-w-[150px]"
            />
          </h4>
        </div>

        <div className="relative mb-8">
          <SliderStyled
            thumbcoloroverride="#e1e1e1"
            trackcoloroverride="#121212"
            step={amountMin}
            min={amountMin}
            max={amountMax}
            value={[sliderValue]}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            colorScheme="light"
          />
        </div>
      </div>

      <div className="md:px-6 md:pb-6">
        <ProjectCompoundedRevenueChart chartData={chartData} variant="white" />
      </div>
    </>
  )
}
